import './ComparisonFieldsPicker.scss';
import React, { useMemo, useState } from 'react';
import { Popover } from '@mantine/core';
import { MdInfoOutline, MdKeyboardArrowDown, MdTune } from 'react-icons/md';
import Checkbox from '@srm/ui/checkbox';
import { IComparisonFieldSetting } from '@models/winnerSelection/ITableDemand';

export interface IComparisonBaseField {
    key: string;
    title: string;
    description: string;
    comparison: IComparisonFieldSetting['comparison'];
}

interface IProps {
    fields: IComparisonFieldSetting[];
    selectedKeys: string[];
    maxSelected: number;
    baseFields: IComparisonBaseField[];
    onChange: (keys: string[]) => void;
}

const comparisonLabels: Partial<Record<IComparisonFieldSetting['comparison'], string>> = {
    min: '↓ минимум',
    max: '↑ максимум',
    target: '◎ к потребности',
};

// Fullscreen-обёртка WinnerSelection занимает слой 9999. Popover рендерится
// порталом в body, поэтому его стандартный слой Mantine (300) оказывается под
// полноэкранным компонентом и дропдаун не виден.
const COMPARISON_FIELDS_POPOVER_Z_INDEX = 10000;

const getSelectionHint = (maxSelected: number) => {
    if (maxSelected === 1) return 'Выберите одно дополнительное поле';
    if (maxSelected === 2) return 'Выберите до двух дополнительных полей';
    if (maxSelected === 3) return 'Выберите до трёх дополнительных полей';
    return `Выберите до ${maxSelected} дополнительных полей`;
};

export const ComparisonFieldsPicker: React.FC<IProps> = ({
    fields,
    selectedKeys,
    maxSelected,
    baseFields,
    onChange,
}) => {
    const [opened, setOpened] = useState(false);
    const [showLimit, setShowLimit] = useState(false);
    const selected = useMemo(() => new Set(selectedKeys), [selectedKeys]);
    const selectedTitles = fields.filter((field) => selected.has(field.key)).map((field) => field.title);
    const summary = [...baseFields.map((field) => field.title), ...selectedTitles].join(', ');
    const defaultKeys = fields
        .filter((field) => field.defaultVisible)
        .map((field) => field.key)
        .slice(0, maxSelected);
    const isDefault =
        defaultKeys.length === selectedKeys.length && defaultKeys.every((key, index) => selectedKeys[index] === key);

    const toggle = (key: string) => {
        if (selected.has(key)) {
            setShowLimit(false);
            onChange(selectedKeys.filter((selectedKey) => selectedKey !== key));
            return;
        }
        if (selectedKeys.length >= maxSelected) {
            setShowLimit(true);
            return;
        }
        setShowLimit(false);
        onChange([...selectedKeys, key]);
    };

    return (
        <Popover
            withinPortal
            opened={opened}
            onChange={(nextOpened) => {
                setOpened(nextOpened);
                if (!nextOpened) setShowLimit(false);
            }}
            position="bottom-start"
            withArrow
            shadow="md"
            radius={12}
            width={380}
            zIndex={COMPARISON_FIELDS_POPOVER_Z_INDEX}
        >
            <Popover.Target>
                <button
                    type="button"
                    className="ws-comparison-picker__trigger"
                    aria-expanded={opened}
                    aria-haspopup="dialog"
                    onClick={() => setOpened((value) => !value)}
                >
                    <span className="ws-comparison-picker__icon" aria-hidden>
                        <MdTune size="18px" />
                    </span>
                    <span className="ws-comparison-picker__copy">
                        <span className="ws-comparison-picker__label">Поля сравнения</span>
                        <span className="ws-comparison-picker__summary" title={summary}>
                            {summary}
                        </span>
                    </span>
                    <MdKeyboardArrowDown className="ws-comparison-picker__chevron" size="18px" aria-hidden />
                </button>
            </Popover.Target>
            <Popover.Dropdown className="ws-comparison-picker__dropdown">
                <div className="ws-comparison-picker__heading">
                    <div>
                        <div className="ws-comparison-picker__title">Что показать для сравнения</div>
                        <div className="ws-comparison-picker__subtitle">{getSelectionHint(maxSelected)}</div>
                    </div>
                    <div className="ws-comparison-picker__counter">{`${selectedKeys.length} / ${maxSelected}`}</div>
                </div>
                <div className="ws-comparison-picker__body">
                    <div className="ws-comparison-picker__base-list">
                        {baseFields.map((field) => (
                            <div
                                key={field.key}
                                className="ws-comparison-picker__option ws-comparison-picker__option--base"
                            >
                                <Checkbox
                                    className="ws-comparison-picker__checkbox-control"
                                    checked
                                    disabled
                                    aria-label={`${field.title}, базовое поле`}
                                />
                                <span className="ws-comparison-picker__option-copy">
                                    <span className="ws-comparison-picker__option-title">{field.title}</span>
                                    <span className="ws-comparison-picker__option-description">
                                        {field.description} · Базовое поле
                                    </span>
                                </span>
                                {comparisonLabels[field.comparison] ? (
                                    <span className="ws-comparison-picker__rule">
                                        {comparisonLabels[field.comparison]}
                                    </span>
                                ) : null}
                            </div>
                        ))}
                    </div>
                    <div className="ws-comparison-picker__list">
                        {fields.map((field) => (
                            <label key={field.key} className="ws-comparison-picker__option">
                                <Checkbox
                                    className="ws-comparison-picker__checkbox-control"
                                    checked={selected.has(field.key)}
                                    onChange={() => toggle(field.key)}
                                    aria-label={field.title}
                                />
                                <span className="ws-comparison-picker__option-copy">
                                    <span className="ws-comparison-picker__option-title">{field.title}</span>
                                    {field.hint ? (
                                        <span className="ws-comparison-picker__option-description">{field.hint}</span>
                                    ) : null}
                                </span>
                                {comparisonLabels[field.comparison] ? (
                                    <span
                                        className={`ws-comparison-picker__rule ws-comparison-picker__rule--${field.comparison}`}
                                    >
                                        {comparisonLabels[field.comparison]}
                                    </span>
                                ) : null}
                            </label>
                        ))}
                    </div>
                </div>
                {showLimit ? (
                    <div className="ws-comparison-picker__limit" role="status">
                        <MdInfoOutline size="18px" aria-hidden />
                        <span>Можно выбрать не больше {maxSelected} дополнительных полей</span>
                    </div>
                ) : null}
                <div className="ws-comparison-picker__footer">
                    <button
                        type="button"
                        className="ws-comparison-picker__reset"
                        disabled={isDefault}
                        onClick={() => {
                            setShowLimit(false);
                            onChange(defaultKeys);
                        }}
                    >
                        Вернуть по умолчанию
                    </button>
                </div>
            </Popover.Dropdown>
        </Popover>
    );
};
